'use client';

import { type MouseEvent, memo, useCallback } from 'react';

import { ListItemRowBase } from '~/components/list/ListItemRowBase';
import { ListItemRowThumbnail } from '~/components/list/ListItemRowThumbnail';
import { type ListViewColumnField } from '~/constants/listViewColumns';
import { type BoardListViewItem } from '~/utils/formatItemsListResponse';
import { getBoardListViewRowInfo } from '~/utils/listViewRowInfo';

export type BoardListItemRowProps = {
  isSelected?: boolean;
  item: BoardListViewItem;
  onClick?: (item: BoardListViewItem, event: MouseEvent<HTMLButtonElement>) => void;
  onDoubleClick?: (item: BoardListViewItem, event: MouseEvent<HTMLButtonElement>) => void;
  visibleColumns: ListViewColumnField[];
};

export const BoardListItemRow = memo(
  ({ isSelected, item, onClick, onDoubleClick, visibleColumns }: BoardListItemRowProps) => {
    const title = item.board.title;
    const testId = `list-board-${item.id}`;

    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onClick?.(item, event);
      },
      [item, onClick],
    );

    const handleDoubleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onDoubleClick?.(item, event);
      },
      [item, onDoubleClick],
    );

    return (
      <ListItemRowBase
        actionTestId={`${testId}-action`}
        isSelected={isSelected}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        rowInfo={getBoardListViewRowInfo(item.board)}
        testId={testId}
        thumbnail={<ListItemRowThumbnail alt={title} src={item.board.thumbnails?.[0]} />}
        title={title}
        visibleColumns={visibleColumns}
      />
    );
  },
);

BoardListItemRow.displayName = 'BoardListItemRow';
